import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	UserIntegrationField,
	type IUserIntegration,
} from './userIntegrationSchema';

export const USER_INTEGRATION_SORTABLE_FIELDS = [
	UserIntegrationField.name,
	UserIntegrationField.creationDate,
	UserIntegrationField.modDate,
	UserIntegrationField.actionSummaries,
] as const;

export const UserIntegrationSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(USER_INTEGRATION_SORTABLE_FIELDS),
});

export type UserIntegrationSort = z.infer<typeof UserIntegrationSortSchema>;

export const UserIntegrationSortableField = z.enum(
	USER_INTEGRATION_SORTABLE_FIELDS,
).enum;

export const USER_INTEGRATION_DEFAULT_SORT_FIELD =
	UserIntegrationSortableField.name;

export const USER_INTEGRATION_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getUserIntegrationDefaultSort = (
	sort: Partial<UserIntegrationSort> = {},
): UserIntegrationSort => ({
	field: getUserIntegrationSortField({sortField: sort.field}),
	order: getUserIntegrationSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getUserIntegrationSortField = (
	params: Omit<
		GetSortFieldParams<(typeof USER_INTEGRATION_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: USER_INTEGRATION_SORTABLE_FIELDS,
		defaultSortField: USER_INTEGRATION_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getUserIntegrationSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: USER_INTEGRATION_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const editUserIntegrationEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IUserIntegration,
			(typeof USER_INTEGRATION_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: USER_INTEGRATION_SORTABLE_FIELDS,
	});
